/* eslint-disable max-statements */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { isErrorWithMessage } from '../../helper';
import PipelineApi from '../../apis/pipeline';
import { RootState } from '../../redux/store';
import { settingAllStages } from '../slices/stage';
import { settingDonutChartData } from '../slices/chart';
import { DuplicatePipelineParams, EditPipelineParams, PipelineParams, PipelineResponse } from '../../params/pipeline';
import { resettingPipelineLoader, settingSelectedPipeline } from '../slices/pipeline';

export const fetchPipelineTemplatesThunk = createAsyncThunk(
    'pipeline/templates',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await PipelineApi.fetchPipelineTemplates(state.app.token);
            if (response?.success === 1) {
                return response?.templates;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const getSinglePipelineDetailThunk = createAsyncThunk(
    'pipeline/single',
    async (id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: PipelineResponse = await PipelineApi.getSinglePipelineDetail(state.app.token, id) as PipelineResponse;
            if (response?.success === 1) {
                dispatch(settingAllStages(response.pipeline ? response?.pipeline?.pipeline_stages || [] : []));
                dispatch(settingDonutChartData({ selectedPipeline: response.pipeline }));
                return response?.pipeline;
            }
            // await dispatch(getAllPipelinesThunk()).unwrap();
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const getAllPipelinesThunk = createAsyncThunk(
    'pipeline/getAll',
    async (client_id: number, { dispatch, rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const response: PipelineResponse = await PipelineApi.getPipelines(state.app.token, client_id);
            if (response?.success === 1 && (window.location.pathname.includes('/app/leads') || window.location.pathname.includes('/lead-management'))) {
                return {
                    pipelines: response.pipelines,
                    success: response.success
                };
            }
            if (response?.success === 1) {
                if (response.pipelines && response?.pipelines?.length === 0) {
                    dispatch(settingSelectedPipeline(null));
                } else if (state.pipeline.selectedPipeline) {
                    const selectedPipeline = response.pipelines?.find((pipe) => pipe.id === state.pipeline.selectedPipeline?.id);
                    const defaultPipeline = response.pipelines?.find((item) => item.is_default === 1);
                    if (!selectedPipeline && response.pipelines && defaultPipeline) {
                        dispatch(settingSelectedPipeline(defaultPipeline));
                    } else if (response.pipelines && !selectedPipeline && !defaultPipeline) {
                        dispatch(settingSelectedPipeline(response?.pipelines[0]));
                    } else {
                        dispatch(settingSelectedPipeline(selectedPipeline));
                    }
                    await dispatch(getSinglePipelineDetailThunk(state.pipeline.selectedPipeline.id)).unwrap();
                } else if (response.pipelines && response.pipelines.length > 0) {
                    const defaultPipeline = response.pipelines?.find((item) => item.is_default === 1);
                    if (defaultPipeline) {
                        dispatch(settingSelectedPipeline(defaultPipeline));
                        await dispatch(getSinglePipelineDetailThunk(defaultPipeline.id)).unwrap();
                    } else {
                        dispatch(settingSelectedPipeline(response?.pipelines[0]));
                    await dispatch(getSinglePipelineDetailThunk(response?.pipelines[0].id)).unwrap();
                    }
                }
                return {
                    pipelines: response.pipelines,
                    success: response.success
                };
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const createPipelineThunk = createAsyncThunk(
    'pipeline/create',
    async ({ name, is_default, deal_default_value, client_id, template_id }: PipelineParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const details = {
                name,
                is_default,
                deal_default_value,
                client_id,
                template_id
            };
            const response: PipelineResponse = await PipelineApi.createPipeline(state.app.token, details);
            if (response?.success === 1) {
                dispatch(settingSelectedPipeline(response.pipeline));
                await dispatch(getAllPipelinesThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request Failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const editPipelineThunk = createAsyncThunk(
    'pipeline/edit',
    async ({ data, client_id }: { data: EditPipelineParams, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await PipelineApi.editPipeline(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(getAllPipelinesThunk(client_id)).unwrap();
                await dispatch(getSinglePipelineDetailThunk(state.pipeline.selectedPipeline?.id as number)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const deletePipelineThunk = createAsyncThunk(
    'pipeline/delete',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await PipelineApi.deletePipeline(state.app.token, state.pipeline.selectedPipeline?.id as number);
            if (response?.success === 1) {
                dispatch(settingSelectedPipeline(null));
                await dispatch(getAllPipelinesThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const duplicatePipelineThunk = createAsyncThunk(
    'pipeline/duplicate',
    async ({ data, client_id }: { data: DuplicatePipelineParams, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await PipelineApi.duplicatePipeline(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(getAllPipelinesThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const bulkDealTransferThunk = createAsyncThunk(
    'pipeline/bulk-transfer',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await PipelineApi.bulkDealTransfer(state.app.token, state.pipeline.selectedDeals, state.pipeline.selectedStageForBulkEdit);
            if (response?.success === 1) {
                await dispatch(getAllPipelinesThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const bulkDealDeleteThunk = createAsyncThunk(
    'pipeline/bulk-delete',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await PipelineApi.bulkDealDelete(state.app.token, state.pipeline.selectedDeals);
            if (response?.success === 1) {
                await dispatch(getAllPipelinesThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);

export const bulkDealArchiveThunk = createAsyncThunk(
    'pipeline/bulk-archive',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await PipelineApi.bulkDealArchive(state.app.token, state.pipeline.selectedDeals);
            if (response?.success === 1) {
                await dispatch(getAllPipelinesThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Updating deal order failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingPipelineLoader());
        }
    }
);